import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Icon from 'components/AppIcon';
import { logError } from '../../../utils/logError';
import { validateEmail, validatePasswordStrength, getStrengthMeta } from '../../../utils/validation';

const PasswordStrengthMeter = ({ password }) => {
  const { score, criteria, feedback } = validatePasswordStrength(password || '');
  const meta = getStrengthMeta(score);

  if (!password) return null;

  const requirements = [
    { key: 'length', label: 'At least 8 characters' },
    { key: 'uppercase', label: 'One uppercase letter' },
    { key: 'lowercase', label: 'One lowercase letter' },
    { key: 'number', label: 'One number' },
    { key: 'special', label: 'One special character (!@#$%^&*)' },
  ];

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5]?.map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${
              i <= meta.bars ? meta.barColor : 'bg-border'
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${meta.textColor}`}>
          {score > 0 ? meta.label : ''}
        </span>
        <span className="text-xs text-muted-foreground">{score}/5 requirements</span>
      </div>
      <ul className="mt-2 space-y-1">
        {requirements?.map((req) => (
          <li key={req?.key} className="flex items-center gap-1.5 text-xs">
            <Icon
              name={criteria?.[req?.key] ? 'CheckCircle' : 'Circle'}
              size={12}
              color={criteria?.[req?.key] ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
            />
            <span className={criteria?.[req?.key] ? 'text-success' : 'text-muted-foreground'}>
              {req?.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const RegisterForm = () => {
  console.log('[RegisterForm] RegisterForm rendered');
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const validate = () => {
    console.log('[RegisterForm] validate() called');
    const newErrors = {};

    console.log('[RegisterForm] About to validate email — value:', form?.email);
    try {
      if (!form?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else {
        console.log('[RegisterForm] Calling validateEmail() from validation.js');
        const emailValid = validateEmail(form?.email);
        console.log('[RegisterForm] validateEmail() returned:', emailValid);
        if (!emailValid) {
          newErrors.email = 'Invalid email format';
        }
      }
    } catch (err) {
      console.error('[RegisterForm] validate() email check THREW:', err?.name, err?.message, err?.stack);
      newErrors.email = 'Email validation error';
    }

    console.log('[RegisterForm] About to validate password strength');
    try {
      if (!form?.password) {
        newErrors.password = 'Password is required';
      } else {
        console.log('[RegisterForm] Calling validatePasswordStrength() from validation.js');
        const { isValid, feedback } = validatePasswordStrength(form?.password);
        console.log('[RegisterForm] validatePasswordStrength() returned — isValid:', isValid, 'feedback:', feedback);
        if (!isValid) {
          newErrors.password = feedback?.[0] || 'Password does not meet all requirements';
        }
      }
    } catch (err) {
      console.error('[RegisterForm] validate() password strength THREW:', err?.name, err?.message, err?.stack);
      newErrors.password = 'Password validation error';
    }

    console.log('[RegisterForm] About to check password match');
    if (!form?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (form?.password !== form?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!form?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept terms and conditions';
    }

    console.log('[RegisterForm] validate() returning errors:', newErrors);
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    if (name === 'email' && value && !validateEmail(value)) {
      setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
    }
    if (name === 'confirmPassword' && value && form?.password !== value) {
      setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
    }
    if (name === 'password' && value) {
      const { isValid, feedback } = validatePasswordStrength(value);
      if (!isValid) {
        setErrors((prev) => ({ ...prev, password: feedback?.[0] || 'Password does not meet all requirements' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[RegisterForm] handleSubmit called — email:', form?.email);
    try {
      console.log('[RegisterForm] handleSubmit: About to validate email');
      console.log('[RegisterForm] handleSubmit: About to validate password strength');
      console.log('[RegisterForm] handleSubmit: About to check password match');
      const validationErrors = validate();
      if (Object.keys(validationErrors)?.length > 0) {
        setErrors(validationErrors);
        return;
      }
      setLoading(true);
      setErrors({});
      try {
        console.log('[RegisterForm] handleSubmit: About to call supabase.auth.signUp for email:', form?.email?.trim()?.toLowerCase());
        const { data, error } = await supabase?.auth?.signUp({
          email: form?.email?.trim()?.toLowerCase(),
          password: form?.password,
          options: {
            emailRedirectTo: undefined,
            data: { username: form?.email?.split('@')?.[0], email_confirmed: true },
          },
        });
        if (error) {
          const msg = error?.message?.toLowerCase();
          if (msg?.includes('already registered') || msg?.includes('already exists') || msg?.includes('email address is already')) {
            setErrors({ email: 'An account with this email already exists. Try signing in instead.' });
          } else if (msg?.includes('invalid email')) {
            setErrors({ email: 'Please enter a valid email address.' });
          } else if (msg?.includes('password')) {
            setErrors({ password: error?.message });
          } else if (msg?.includes('network') || msg?.includes('fetch')) {
            setErrors({ general: 'Network error. Please check your connection and try again.' });
          } else {
            setErrors({ general: error?.message || 'Registration failed. Please try again.' });
          }
          logError({ error, componentName: 'RegisterForm', actionName: 'handleSubmit', screenName: 'register', context: { email: form?.email?.trim() } });
          return;
        }
        setSuccess(true);
      } catch (err) {
        console.error('[RegisterForm] supabase.auth.signUp THREW — name:', err?.name, '— message:', err?.message, '— stack:', err?.stack);
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
        logError({ error: err, componentName: 'RegisterForm', actionName: 'handleSubmit', screenName: 'register' });
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error('[RegisterForm] handleSubmit outer CAUGHT ERROR — name:', err?.name, '— message:', err?.message, '— stack:', err?.stack);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: form?.email?.trim()?.toLowerCase(),
      });
      if (error) {
        setResendMsg('Failed to resend. Please try again.');
      } else {
        setResendMsg('Verification email resent! Check your inbox.');
      }
    } catch {
      setResendMsg('Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 border border-success/30 mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">Account Created!</h3>
        <p className="text-sm text-muted-foreground mb-1">Your account has been created successfully.</p>
        <p className="text-sm font-semibold text-primary mb-4">{form?.email}</p>
        <p className="text-xs text-muted-foreground mb-6">
          You can now sign in and start building your empire.
        </p>
        <div className="space-y-3">
          <Link
            to="/sign-in"
            className="block w-full py-2.5 rounded-lg bg-primary text-zinc-950 text-sm font-semibold text-center hover:bg-primary/90 transition-all"
          >
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  const { score: pwScore } = validatePasswordStrength(form?.password || '');
  const hasFormErrors = Object.values(errors)?.some(Boolean);

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4" aria-label="Create account form">
      {/* General error */}
      {errors?.general && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/30" role="alert" aria-live="polite">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 shrink-0" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}
      {/* Email */}
      <div>
        <label htmlFor="register-email" className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
        <div className="relative">
          <Icon name="Mail" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="register-email"
            type="email"
            name="email"
            value={form?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="player@empirex.com"
            disabled={loading}
            autoComplete="email"
            aria-label="Email address"
            aria-invalid={!!errors?.email}
            aria-describedby={errors?.email ? 'register-email-error' : undefined}
            className={`w-full bg-surface border rounded-lg pl-10 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
              errors?.email ? 'border-error focus:border-error' : 'border-border focus:border-primary/50'
            } disabled:opacity-60`}
          />
        </div>
        {errors?.email && (
          <p id="register-email-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />{errors?.email}
          </p>
        )}
      </div>
      {/* Password */}
      <div>
        <label htmlFor="register-password" className="block text-sm font-medium text-foreground mb-1.5">Password</label>
        <div className="relative">
          <Icon name="Lock" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="register-password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={form?.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Create a strong password"
            disabled={loading}
            autoComplete="new-password"
            aria-label="Password"
            aria-invalid={!!errors?.password}
            aria-describedby={errors?.password ? 'register-password-error' : 'password-requirements'}
            className={`w-full bg-surface border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
              errors?.password ? 'border-error focus:border-error' : 'border-border focus:border-primary/50'
            } disabled:opacity-60`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
        {errors?.password && (
          <p id="register-password-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />{errors?.password}
          </p>
        )}
        <div id="password-requirements"><PasswordStrengthMeter password={form?.password} /></div>
      </div>
      {/* Confirm Password */}
      <div>
        <label htmlFor="register-confirm-password" className="block text-sm font-medium text-foreground mb-1.5">Confirm Password</label>
        <div className="relative">
          <Icon name="Lock" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            id="register-confirm-password"
            type={showConfirm ? 'text' : 'password'}
            name="confirmPassword"
            value={form?.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Repeat your password"
            disabled={loading}
            autoComplete="new-password"
            aria-label="Confirm password"
            aria-invalid={!!errors?.confirmPassword}
            aria-describedby={errors?.confirmPassword ? 'register-confirm-error' : undefined}
            className={`w-full bg-surface border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
              errors?.confirmPassword ? 'border-error focus:border-error' : 'border-border focus:border-primary/50'
            } disabled:opacity-60`}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
          >
            <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
        {errors?.confirmPassword && (
          <p id="register-confirm-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />{errors?.confirmPassword}
          </p>
        )}
        {form?.confirmPassword && form?.password === form?.confirmPassword && !errors?.confirmPassword && (
          <p className="mt-1 text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle" size={12} />Passwords match
          </p>
        )}
      </div>
      {/* Terms */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={form?.acceptTerms}
            onChange={handleChange}
            disabled={loading}
            aria-label="Accept terms of service"
            aria-invalid={!!errors?.acceptTerms}
            aria-describedby={errors?.acceptTerms ? 'register-terms-error' : undefined}
            className="mt-0.5 w-4 h-4 rounded border-border bg-surface text-primary focus:ring-2 focus:ring-primary/50 focus:ring-offset-0 cursor-pointer"
          />
          <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
            I agree to the{' '}
            <span className="text-primary hover:text-primary/80 cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary hover:text-primary/80 cursor-pointer">Privacy Policy</span>
          </span>
        </label>
        {errors?.acceptTerms && (
          <p id="register-terms-error" className="mt-1 text-xs text-error flex items-center gap-1" role="alert">
            <Icon name="AlertCircle" size={12} />{errors?.acceptTerms}
          </p>
        )}
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={loading || hasFormErrors}
        aria-label="Create account"
        aria-busy={loading}
        className="w-full py-3 rounded-lg bg-primary text-zinc-950 font-semibold text-sm hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        {loading ? (
          <><Icon name="Loader2" size={16} className="animate-spin" /> Creating Account...</>
        ) : (
          <><Icon name="UserPlus" size={16} /> Create Account</>
        )}
      </button>
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/sign-in" className="text-primary hover:text-primary/80 font-medium transition-colors">
          Sign in
        </Link>
      </p>
    </form>
  );
};

export default RegisterForm;
